/**
 * Contract Generator Utility
 * Generates HTML contract from booking data
 */

/**
 * Get ordinal suffix for day (1st, 2nd, 3rd, etc.)
 */
const getOrdinalSuffix = (day) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

/**
 * Format date as "1st day of February 2026"
 */
const formatLeaseDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  const year = date.getFullYear();
  return `${day}${getOrdinalSuffix(day)} day of ${month} ${year}`;
};

/**
 * Format duration intelligently
 */
const formatDuration = (months, days) => {
  if (months === 0) {
    return `${days} day${days !== 1 ? 's' : ''}`;
  } else if (months === 1 && days <= 31) {
    return `one month (${days} day${days !== 1 ? 's' : ''})`;
  } else if (months > 1 && months < 12) {
    return `${months} months (${days} day${days !== 1 ? 's' : ''})`;
  } else if (months === 12) {
    return `an Academic Year (${months} months)`;
  } else {
    return `${months} month${months !== 1 ? 's' : ''} (${days} day${days !== 1 ? 's' : ''})`;
  }
};

export const generateContractHTML = (contractData) => {
  const {
    student,
    landlord,
    property,
    bmo,
    lease,
    contract,
    booking_id
  } = contractData;

  return generateContractHTMLForModal(contractData);
};

// Generate contract HTML for modal display (no accept/decline buttons)
export const generateContractHTMLForModal = (contractData) => {
  const {
    student,
    landlord,
    property,
    bmo,
    lease,
    contract
  } = contractData;

  return `<style>
        body {
            font-family: 'Georgia', 'Times New Roman', serif;
            line-height: 1.8;
            color: #333;
        }
        
        .content {
            padding: 40px 50px;
        }
        
        h1, h2, h3 {
            color: #36669f;
            margin-top: 25px;
            margin-bottom: 15px;
        }
        
        p {
            margin-bottom: 15px;
            text-align: justify;
            font-size: 15px;
        }
        
        strong {
            color: #36669f;
            font-weight: 600;
        }
        
        .data-bold {
            font-weight: 600;
            color: #333;
        }
    </style>
    <div class="content">
<p><strong>STUDENT LEASE AGREEMENT</strong></p>
<p>This Lease Agreement ("Agreement") is made and entered into on this ${contract.day} day of ${contract.month} ${contract.year}, by and between:</p>
<p><strong>1. The Landlord (Accommodation Provider):</strong><br>
<span class="data-bold">${landlord.type === 'entity' && landlord.company_name ? landlord.company_name : landlord.name || 'N/A'}</span><br>
${landlord.type === 'entity' && landlord.representative_name ? `<span class="data-bold">Represented by: ${landlord.representative_name}</span><br>` : ''}
<span class="data-bold">${landlord.address || 'N/A'}</span><br>
<span class="data-bold">${landlord.email || 'N/A'}</span><br>
<span class="data-bold">${landlord.phone || 'N/A'}</span></p>
<p><strong>2. The Tenant (Student):</strong><br>
<span class="data-bold">${student.full_name || 'N/A'}</span><br>
<span class="data-bold">Student Number: ${student.student_number || 'N/A'}</span><br>
<span class="data-bold">${student.institution || 'N/A'}</span><br>
<span class="data-bold">${student.campus || 'N/A'}</span><br>
<span class="data-bold">${student.email || 'N/A'}</span><br>
<span class="data-bold">${student.phone || 'N/A'}</span></p>
<p><strong>3. The Bursary Management Organization (BMO):</strong><br>
<span class="data-bold">${bmo.name || 'N/A'}</span><br>
<span class="data-bold">${bmo.address || 'N/A'}</span></p>
<p><strong>4. Facilitated by:</strong>
<strong>Lafunza Student Services</strong>
(acting as an intermediary management service connecting The Accommodation Provider, The Student, and The Bursary Management Organization).</p>
<p><strong>1. Premises</strong></p>
<p>The Landlord hereby leases to the Tenant a self-contained unit located at <strong>${property.address || 'N/A'}${property.unit !== 'N/A' ? ', Unit ' + property.unit : ''}</strong> ("the Premises").</p>
<p><strong>2. Lease Term</strong></p>
<p>The lease shall commence on the ${formatLeaseDate(lease.start_date)}, and shall continue for ${formatDuration(lease.duration_months, lease.duration_days)} unless terminated earlier in accordance with this Agreement.</p>
<p><strong>3. Rent and Payment</strong></p>
<p>3.1 Rent is payable by the <strong>Bursary Management Organization (BMO)</strong> on behalf of the Tenant and shall be disbursed <strong>${lease.payment_frequency}</strong> directly to the Landlord less any agreed deductions.</p>
<p>3.2 The Landlord acknowledges that Lafunza Student Services facilitates the student accommodation arrangement but is <strong>not a party</strong> to the financial transaction between the BMO and the Landlord.</p>
<p>3.3 The total rental amount for the Lease Term is <strong>R${lease.total_rent} (South African Rand)</strong>, inclusive of utilities.</p>
<p>3.4 The Landlord agrees that for using services provided by Lafunza Student Services, it shall pay a Platform Fee equivalent to <strong>${lease.platform_fee_percent}% of the total rental amount (R${lease.platform_fee_amount})</strong>, and which shall be deducted by the BMO for onward forwarding to Lafunza Student Services.</p>
<p>3.5 The Landlord shall invoice the BMO within <strong>7 days</strong> of Tenant checking in. The invoice shall be accompanied by a fully completed Tenant Check-In Checklist.</p>
<p>3.6 The BMO shall pay the Landlord the amount due for the quarter, within <strong>21 days</strong> of receiving the Invoice from the Landlord.</p>
<p>3.7 The BMO shall pay Lafunza the applicable Platform Fee deducted from the landlord within <strong>21 days</strong> of the first month of the quarter.</p>
<p><strong>4. Security Deposit</strong></p>
<p>4.1 The Landlord may not charge any security deposit.</p>
<p><strong>5. Utilities and Services</strong></p>
<p>The rent includes the following services and utilities:</p>
<p>Water</p>
<p>Electricity</p>
<p>Wi-Fi / Internet Access</p>
<p>Cleaning Services (as per house rules)</p>
<p>No additional utility charges shall be levied unless otherwise agreed in writing.</p>
<p><strong>6. Condition of Premises</strong></p>
<p>6.1 The Tenant acknowledges having inspected the Premises and confirms it is in good condition at the start of the lease.</p>
<p>6.2 An <strong>entry and exit inspection</strong> shall be conducted jointly by the Tenant and Landlord.</p>
<p>6.3 The Tenant shall keep the Premises clean and in good order and promptly report any damages or maintenance issues.</p>
<p><strong>7. Use of Premises</strong></p>
<p>7.1 The Premises shall be used <strong>solely for residential purposes</strong> by the Tenant and, in the case of shared accommodation, no more than two (2) students per room.</p>
<p>7.2 The Tenant shall comply with all <strong>house rules</strong>, including those relating to noise, cleanliness, and respect for other occupants.</p>
<p><strong>8. Maintenance and Repairs</strong></p>
<p>8.1 The Landlord is responsible for structural maintenance and repairs, including plumbing, electrical, and safety-related issues.</p>
<p>8.2 The Tenant shall be responsible for any damage caused by negligence or misuse.</p>
<p><strong>9. Termination</strong></p>
<p>9.1 Either party may terminate this Agreement with <strong>one (1) month's written notice</strong>.</p>
<p>9.2 The Landlord may terminate immediately in the event of serious breach, including unlawful activities, property damage, or noncompliance with house rules.</p>
<p>9.3 Upon termination, the Tenant shall vacate the Premises and return all keys/access cards.</p>
<p><strong>10. Subletting</strong></p>
<p>The Tenant shall <strong>not sublet</strong> or assign the Premises or any part thereof without the prior written consent of the Landlord.</p>
<p><strong>11. Liability</strong></p>
<p>11.1 The Landlord shall not be liable for any loss, theft, or damage to the Tenant's personal belongings.</p>
<p>11.2 The Tenant is advised to take reasonable care to secure personal property.</p>
<p><strong>12. Role of Lafunza Platform</strong></p>
<p>12.1 Lafunza acts as a <strong>facilitating platform</strong> and <strong>record-keeping intermediary</strong> between the Tenant, Landlord, and BMO.</p>
<p>12.2 Lafunza shall not be liable for any disputes arising directly between the Tenant and the Landlord but may mediate where possible.</p>
<p>12.3 All payments and communications relating to rent and occupancy shall be managed through the Lafunza system for tracking and compliance purposes.</p>
<p><strong>13. Governing Law</strong></p>
<p>This Agreement shall be governed by and construed in accordance with the <strong>laws of the Republic of South Africa</strong>, including the <strong>Rental Housing Act (No. 50 of 1999)</strong> and the <strong>Consumer Protection Act (No. 68 of 2008)</strong>.</p>
<p><strong>14. Dispute Resolution</strong></p>
<p>14.1 Any disputes arising out of or in connection with this Agreement shall first be referred to mediation facilitated through the Lafunza Student Services.</p>
<p>14.2 If unresolved, either party may refer the matter to the <strong>Rental Housing Tribunal</strong> or competent court.</p>
<br>
<p><strong>15. Signatures</strong></p>
<br>
<p><strong>Landlord:</strong>
Name: _______________________________
Signature: ___________________________
Date: _______________________________</p>
<br>
<p><strong>Tenant:</strong>
Name: _______________________________
Signature: ___________________________
Date: _______________________________</p>
<br>
<p><strong>Facilitated by </strong><strong>La</strong><strong>f</strong><strong>unza</strong><strong> Student </strong><strong>Services</strong>
Website: [www.lafunza.com]
Email: support@lafunza.com</p>
<br>
<br>
    </div>`;
};
